// Окно беседы UI v3 (README §2, §2а, §2б, §2в; ui-plan пп.8–9): одна сессия
// на всю рабочую область, повторяет консоль claude code на макбуке —
// системный моно, глифы «>», «⏺», «⎿», сетка 2/5 знаков, под рамкой ввода
// строка «⏵⏵ режим прав» + модель/контекст, «✻ Работаю… (esc — прервать)».
//
// Каркас (рост поля, ручка, прилипание лога к низу) — общий CliWindow/CliLog/
// CliInput; здесь наполнение: стрим сессии, строки лога, слэш-команды,
// `!`-режим, «@»-пути, история ввода, Shift+Tab, Esc/Esc Esc, вложения,
// голос, заметка «в контексте», карточки разрешений и вопросов.

import { useCallback, useEffect, useRef, useState } from 'react'
import type { ClipboardEvent, DragEvent, KeyboardEvent, ReactNode } from 'react'
import { api } from '../../api'
import { PERMISSION_MODE_LABEL } from '../../protocol'
import type { AttachmentMeta, CommandInfo, FileEntry, PermissionMode, SessionState } from '../../protocol'
import { MOBILE_QUERY, useMediaQuery } from '../../lib/useMediaQuery'
import { describeError, useAppStore } from '../../store/appStore'
import { attachmentFiles, useLogStore, usePane, VOICE_BARS } from '../../store/logStore'
import type { DraftAttachment } from '../../store/logStore'
import { SessionStream, deliverInput } from '../../transport/ws'
import type { PermissionOptions } from '../../transport/ws'
import { cliApi } from '../../transport/cliApi'
import { CliAttachButton, CliInput, CliLog, CliVoiceButton, CliWindow } from '../../ui/Cli'
import type { CliLogHandle } from '../../ui/Cli'
import { Menu } from '../../ui/Popover'
import AgentsBlock from '../AgentsBlock'
import BusyLine from '../BusyLine'
import LineView from '../LineView'
import SaveNoteDialog from '../SaveNoteDialog'
import VoiceBar from '../VoiceBar'
import VoicePending from '../VoicePending'
import { extOf, humanSize } from './AttachRow'
import {
  commandRoute, expandPastes, filterCommands, getDraftPastes, isBigPaste, loadCommands, loadHistory,
  MODE_CYCLE, nextMode, PANEL_COMMANDS, pasteToken, pushHistory, setDraftPastes, SOURCE_LABEL, tokenAt,
  usedPastes,
} from './commands'
import type { HistEntry } from './commands'
import '../log.css'
import './cli.css'

/** Сколько строк лога держать в DOM; раньше — по кнопке «показать раньше». */
const WINDOW_LINES = 400
/** Два Esc подряд за это время — очистить поле (ui-plan п.9). */
const DOUBLE_ESC_MS = 650
/** Максимум вложений, пока сервер не сказал свой лимит. */
const DEFAULT_LIMITS = { maxBytes: 50 * 1024 * 1024, maxFiles: 10, blocked: [] as string[] }

type Suggest =
  | { kind: 'cmd'; items: CommandInfo[]; index: number }
  | { kind: 'file'; items: FileEntry[]; index: number; start: number; end: number }

export interface CliSessionProps {
  sessionId: string
  /** chat — Беседы (с `!`-режимом); channel — канал в Мессенджерах */
  variant?: 'chat' | 'channel'
  /** подсказка в пустом поле */
  placeholder?: string
  /** заметка «в контексте» (§2): плашка над полем, путь уходит агенту с первой репликой */
  ctxNote?: { path: string; title: string; sent: boolean } | null
  onDropNote?: () => void
  onNoteSent?: () => void
  /** что показать в пустом логе */
  empty?: ReactNode
}

function uid(): string {
  const c = globalThis.crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  return `a${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
}

/** Картинка из буфера приходит безымянной — даём имя со временем вставки. */
function namedPaste(file: File): File {
  if (file.name && !/^image\.\w+$/i.test(file.name)) return file
  const ext = (file.type.split('/')[1] || 'png').replace(/[^a-z0-9]/gi, '') || 'png'
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  const stamp = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`
  return new File([file], `screenshot-${stamp}.${ext}`, { type: file.type })
}

function ctxLabel(percent: number, tokens: number): string {
  if (percent > 0) return `${percent.toFixed(0)}% ctx`
  if (tokens > 0) return `${Math.round(tokens / 1000)}k ctx`
  return ''
}

function modelLabel(model: string | null | undefined): string {
  if (!model) return ''
  return model.replace(/^claude-/, '').replace(/-\d{8}$/, '').replace('[1m]', ' 1M').replace(/-/g, ' ')
}

export default function CliSession({
  sessionId, variant = 'chat', placeholder, ctxNote, onDropNote, onNoteSent, empty,
}: CliSessionProps) {
  const mobile = useMediaQuery(MOBILE_QUERY)
  const streamRef = useRef<SessionStream | null>(null)
  const logRef = useRef<CliLogHandle>(null)
  const taRef = useRef<HTMLTextAreaElement | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)
  const lastEsc = useRef(0)
  const histPos = useRef<number | null>(null)
  const histDraft = useRef<HistEntry>({ text: '' })
  const aborts = useRef(new Map<string, () => void>())
  const dragDepth = useRef(0)
  const fileQuery = useRef('')
  const fileTimer = useRef(0)

  const session = usePane(sessionId, (s) => s.session)
  const lines = usePane(sessionId, (s) => s.lines)
  const conn = usePane(sessionId, (s) => s.conn)
  const draft = usePane(sessionId, (s) => s.draft)
  const attachments = usePane(sessionId, (s) => s.attachments)
  const voicePhase = usePane(sessionId, (s) => s.voice.phase)
  const setDraft = useLogStore((s) => s.setDraft)
  const pushLocal = useLogStore((s) => s.pushLocal)
  const openNote = useAppStore((s) => s.openNote)

  const [shownFrom, setShownFrom] = useState<number | null>(null)
  const [suggest, setSuggest] = useState<Suggest | null>(null)
  const [commands, setCommands] = useState<CommandInfo[]>(PANEL_COMMANDS)
  const [notice, setNotice] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const [limits, setLimits] = useState(DEFAULT_LIMITS)
  const [modeMenu, setModeMenu] = useState<HTMLElement | null>(null)
  const [reviving, setReviving] = useState(false)
  const [dialog, setDialog] = useState(false)
  const [ultraBusy, setUltraBusy] = useState(false)
  /** bash-режим поля: «!» в пустом поле переключает, как в Claude Code (ui-plan п.8) */
  const [bang, setBang] = useState(false)

  const state: SessionState = session?.state ?? 'idle'
  const mode: PermissionMode = session?.permissionMode ?? 'default'
  const exited = state === 'exited'
  const busy = state === 'running' || state === 'waiting_permission' || !!session?.bashRunning
  const bashMode = variant === 'chat' && bang

  // ---------------- стрим ----------------

  useEffect(() => {
    const stream = new SessionStream(sessionId)
    streamRef.current = stream
    stream.start()
    setShownFrom(null)
    setBang(false)
    histPos.current = null
    return () => {
      stream.dispose()
      streamRef.current = null
    }
  }, [sessionId])

  // ограничения вложений и список команд — один раз на сессию
  useEffect(() => {
    let alive = true
    cliApi.limits(sessionId).then((l) => { if (alive) setLimits(l) }, () => {})
    loadCommands(sessionId).then((list) => { if (alive) setCommands(list) }, () => {})
    return () => { alive = false }
  }, [sessionId, session?.pid])

  useEffect(() => {
    if (!mobile) taRef.current?.focus()
  }, [sessionId, mobile])

  // ---------------- действия ----------------

  const stop = useCallback(() => {
    void cliApi.interrupt(sessionId).catch(() => streamRef.current?.interrupt())
  }, [sessionId])

  const respond = useCallback((id: string, behavior: 'allow' | 'deny', opts?: PermissionOptions) => {
    streamRef.current?.respondPermission(id, behavior, opts)
  }, [])

  const setMode = useCallback((next: PermissionMode) => {
    streamRef.current?.setPermissionMode(next)
  }, [])

  const relaunch = useCallback(async (force: boolean) => {
    setReviving(true)
    try {
      const info = await api.resume(sessionId, force)
      useAppStore.getState().upsertSession(info)
      useLogStore.getState().setConn(sessionId, 'connecting')
      streamRef.current?.dispose()
      const stream = new SessionStream(sessionId)
      streamRef.current = stream
      stream.start()
    } catch (e) {
      setNotice(describeError(e))
    } finally {
      setReviving(false)
    }
  }, [sessionId])

  const toggleUltra = useCallback(async () => {
    if (!session || ultraBusy) return
    setUltraBusy(true)
    try {
      await api.patchSession(sessionId, { ultracode: !session.ultracode })
    } catch (e) {
      setNotice(describeError(e))
    } finally {
      setUltraBusy(false)
    }
  }, [session, sessionId, ultraBusy])

  // ---------------- вложения (§2в) ----------------

  const upload = useCallback((item: DraftAttachment) => {
    const file = attachmentFiles.get(item.id)
    if (!file) return
    const store = useLogStore.getState()
    store.patchAttachment(sessionId, item.id, { status: 'uploading', error: null, progress: 0 })
    const job = cliApi.upload(sessionId, file, (share) => {
      useLogStore.getState().patchAttachment(sessionId, item.id, { progress: share })
    })
    aborts.current.set(item.id, job.abort)
    job.done.then(
      (res) => useLogStore.getState().patchAttachment(sessionId, item.id, {
        status: 'ready', progress: 1, path: res.path, serverId: res.id ?? null,
        name: res.name, size: res.size, mime: res.mime, kind: res.kind, executable: res.executable,
      }),
      (err: unknown) => {
        if ((err as { status?: number }).status === -1) return
        useLogStore.getState().patchAttachment(sessionId, item.id, {
          status: 'error', error: err instanceof Error ? err.message : 'не загрузилось',
        })
      },
    ).finally(() => aborts.current.delete(item.id))
  }, [sessionId])

  const addFiles = useCallback((files: File[]) => {
    if (files.length === 0) return
    const have = useLogStore.getState().sessions[sessionId]?.attachments.length ?? 0
    const room = limits.maxFiles - have
    const problems: string[] = []
    const take: File[] = []
    for (const file of files) {
      const ext = extOf(file.name)
      if (limits.blocked.includes(ext)) { problems.push(`файлы .${ext} прикреплять нельзя`); continue }
      if (file.size > limits.maxBytes) {
        problems.push(`файл больше ${humanSize(limits.maxBytes)} — не прикреплён: ${file.name}`)
        continue
      }
      if (file.size === 0) { problems.push(`файл пустой — не прикреплён: ${file.name}`); continue }
      if (take.length >= room) { problems.push(`не больше ${limits.maxFiles} файлов за сообщение`); break }
      take.push(file)
    }
    setNotice(problems.length ? problems[0] + (problems.length > 1 ? ` (и ещё ${problems.length - 1})` : '') : null)
    const items: DraftAttachment[] = take.map((file) => ({
      id: uid(), name: file.name, size: file.size, path: null, status: 'uploading', error: null,
      progress: 0, mime: file.type, kind: file.type.startsWith('image/') ? 'image' : 'file',
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }))
    items.forEach((item, i) => attachmentFiles.set(item.id, take[i]))
    useLogStore.getState().addAttachments(sessionId, items)
    items.forEach(upload)
  }, [limits, sessionId, upload])

  const removeFile = useCallback((id: string) => {
    aborts.current.get(id)?.()
    const item = useLogStore.getState().sessions[sessionId]?.attachments.find((a) => a.id === id)
    if (item?.preview) URL.revokeObjectURL(item.preview)
    attachmentFiles.delete(id)
    useLogStore.getState().removeAttachment(sessionId, id)
  }, [sessionId])

  const uploading = attachments.some((a) => a.status === 'uploading')
  const ready = attachments.filter((a) => a.status === 'ready')

  // ---------------- отправка ----------------

  const say = useCallback((text: string) => {
    pushLocal(sessionId, { kind: 'warn', gutter: null, text })
  }, [pushLocal, sessionId])

  const runPanelCommand = useCallback((name: string, args: string) => {
    void cliApi.command(sessionId, name, args).then(
      (res) => {
        // /model, /permissions, /clear меняют строку режима — статус придёт стримом
        if (name === 'clear' || name === 'compact') loadCommands(sessionId, true)
        return res
      },
      (err: unknown) => say(`/${name}: ${err instanceof Error ? err.message : 'не выполнилась'}`),
    )
  }, [say, sessionId])

  const clearAfterSend = useCallback(() => {
    setDraftPastes(sessionId, [])
    setDraft(sessionId, '')
    setBang(false)
    setSuggest(null)
    histPos.current = null
    setNotice(null)
  }, [sessionId, setDraft])

  const submit = useCallback(async (override?: string) => {
    const raw = override ?? draft
    const text = raw.trim()
    if (uploading) return
    if (!text && ready.length === 0) return
    // большие вставки: в поле и в логе — токены, агенту — полный текст (как в CLI)
    const pastes = usedPastes(text, getDraftPastes(sessionId))

    // `!команда` — shell в папке сессии, не реплика агенту (ui-plan п.8)
    if (variant === 'chat' && bang) {
      const command = expandPastes(text, pastes)
      if (!command) return
      pushHistory(sessionId, `!${text}`, pastes)
      clearAfterSend()
      logRef.current?.scrollToBottom()
      try {
        await cliApi.bash(sessionId, command)
      } catch (err) {
        say(`!: ${err instanceof Error ? err.message : 'не выполнилась'}`)
      }
      return
    }

    // `/команда` без вложений (README §2а). Куда её — решает список сессии:
    // panel — вывод панели, agent — процессу CLI как есть, local — браузер.
    const slash = /^\/([^\s/]+)(?:\s|$)/.exec(text)
    if (slash && ready.length === 0) {
      const name = slash[1].toLowerCase()
      const args = text.slice(1 + slash[1].length).trim()
      const route = commandRoute(name, commands)
      pushHistory(sessionId, text, pastes)
      clearAfterSend()
      logRef.current?.scrollToBottom()
      if (route === 'local') {
        if (name === 'restart') void relaunch(true)
        else if (name === 'save') {
          if (session?.notePath) say(`беседа уже сохранена: ${session.notePath}`)
          else setDialog(true)
        } else if (name === 'multiagent') void toggleUltra()
        else if (name === 'rename') {
          const title = expandPastes(args, pastes).replace(/\s+/g, ' ').trim()
          if (!title) say('/rename <название> — новое название беседы')
          else {
            void api.patchSession(sessionId, { title }).then(
              (info) => useAppStore.getState().upsertSession(info),
              (err: unknown) => say(`/rename: ${describeError(err)}`),
            )
          }
        }
        return
      }
      if (route === 'panel') {
        runPanelCommand(name, expandPastes(args, pastes))
        return
      }
      if (route === 'agent') {
        deliverInput(sessionId, text, { pastes })
      } else {
        // неизвестная — агенту как текст с пометкой: иначе CLI ответит
        // «Unknown command» и до модели она не дойдёт
        deliverInput(sessionId, `(не команда) ${text}`, { pastes })
      }
      return
    }

    if (exited) {
      setNotice('сессия завершена — «продолжить разговор» поднимет её с того же места')
      return
    }
    const ids = ready.map((a) => a.serverId).filter((x): x is string => !!x)
    // старый сервер без id — путь в тексте, как раньше
    const legacy = ready.filter((a) => !a.serverId && a.path).map((a) => a.path as string)
    const body = legacy.length ? `${legacy.join('\n')}${text ? `\n\n${text}` : ''}` : text
    const shown: AttachmentMeta[] = ready.filter((a) => a.serverId).map((a) => ({
      id: a.serverId as string, name: a.name, size: a.size, mime: a.mime ?? '',
      kind: a.kind ?? 'file', executable: !!a.executable,
    }))
    const note = ctxNote && !ctxNote.sent ? ctxNote.path : undefined
    pushHistory(sessionId, text, pastes)
    deliverInput(sessionId, body, { attachments: ids, note, shown, pastes })
    if (note) onNoteSent?.()
    for (const a of ready) {
      if (a.preview) URL.revokeObjectURL(a.preview)
      attachmentFiles.delete(a.id)
    }
    useLogStore.getState().clearAttachments(sessionId)
    clearAfterSend()
    logRef.current?.scrollToBottom()
  }, [
    bang, clearAfterSend, commands, ctxNote, draft, exited, onNoteSent, ready, relaunch, runPanelCommand, say,
    session?.notePath, sessionId, toggleUltra, uploading, variant,
  ])

  // ---------------- подсказки: «/» и «@» ----------------

  const refreshSuggest = useCallback((value: string) => {
    const ta = taRef.current
    const caret = ta ? ta.selectionStart ?? value.length : value.length
    // «/имя» — пока курсор в первом слове и строк ещё нет
    if (value.startsWith('/') && !/\s/.test(value.slice(0, caret)) && !value.includes('\n')) {
      const items = filterCommands(commands, value.slice(1, caret)).slice(0, 300)
      setSuggest(items.length ? { kind: 'cmd', items, index: 0 } : null)
      return
    }
    const before = value.slice(0, caret)
    const m = /(^|\s)@([^\s@]*)$/.exec(before)
    if (m) {
      const start = before.length - m[2].length - 1
      const q = m[2]
      const token = `${sessionId}:${q}`
      fileQuery.current = token
      window.clearTimeout(fileTimer.current)
      fileTimer.current = window.setTimeout(() => {
        cliApi.files(sessionId, q).then((items) => {
          if (fileQuery.current !== token) return
          setSuggest(items.length ? { kind: 'file', items, index: 0, start, end: caret } : null)
        }, () => setSuggest(null))
      }, 120)
      return
    }
    fileQuery.current = ''
    setSuggest(null)
  }, [commands, sessionId])

  const onChange = useCallback((value: string) => {
    // «!» первым знаком в пустом поле — bash-режим; сам «!» в текст не идёт
    if (variant === 'chat' && !bang && value.startsWith('!')
      && (useLogStore.getState().sessions[sessionId]?.draft ?? '') === '') {
      setBang(true)
      value = value.slice(1)
    }
    setDraft(sessionId, value)
    histPos.current = null
    if (notice && !uploading) setNotice(null)
    refreshSuggest(value)
  }, [bang, notice, refreshSuggest, sessionId, setDraft, uploading, variant])

  const acceptFile = useCallback((entry: FileEntry, s: Extract<Suggest, { kind: 'file' }>) => {
    const value = useLogStore.getState().sessions[sessionId]?.draft ?? ''
    const insert = `@${entry.path}${entry.dir ? '' : ' '}`
    const next = value.slice(0, s.start) + insert + value.slice(s.end)
    setDraft(sessionId, next)
    const pos = s.start + insert.length
    requestAnimationFrame(() => {
      const ta = taRef.current
      if (ta) { ta.focus(); ta.setSelectionRange(pos, pos) }
      if (entry.dir) refreshSuggest(next)
      else setSuggest(null)
    })
  }, [refreshSuggest, sessionId, setDraft])

  const acceptCommand = useCallback((item: CommandInfo, run: boolean) => {
    const value = useLogStore.getState().sessions[sessionId]?.draft ?? ''
    const rest = value.replace(/^\/\S*/, '').trim()
    const text = `/${item.name}${rest ? ` ${rest}` : ''}`
    setSuggest(null)
    // ↵ выполняет сразу, если аргумент не обязателен (<…>); [необязательный] — тоже сразу
    if (run && !item.argHint.startsWith('<')) {
      void submit(text)
      return
    }
    setDraft(sessionId, `${text} `)
    requestAnimationFrame(() => taRef.current?.focus())
  }, [sessionId, setDraft, submit])

  // выбранная подсказка всегда видна в прокручиваемом списке
  useEffect(() => {
    if (!suggest) return
    document.querySelector('.cli-suggest__row.is-on')?.scrollIntoView({ block: 'nearest' })
  }, [suggest])

  // ---------------- клавиши ----------------

  /** Поставить запись истории в поле: «!…» — снова в bash-режиме; вставки — токенами. */
  const recall = useCallback((entry: HistEntry) => {
    setDraftPastes(sessionId, entry.pastes ?? [])
    if (variant === 'chat' && entry.text.startsWith('!')) {
      setBang(true)
      setDraft(sessionId, entry.text.slice(1))
    } else {
      setBang(false)
      setDraft(sessionId, entry.text)
    }
  }, [sessionId, setDraft, variant])

  /** Заменить выделение (или вставить в курсор) нативно — через execCommand:
   *  курсор встаёт за вставкой сразу, Ctrl+Z работает, onChange приходит сам.
   *  Где execCommand нет — руками. */
  const replaceSelection = useCallback((ta: HTMLTextAreaElement, insert: string, from?: number, to?: number) => {
    if (from !== undefined && to !== undefined) ta.setSelectionRange(from, to)
    ta.focus()
    let done = false
    try {
      done = insert
        ? document.execCommand('insertText', false, insert)
        : document.execCommand('delete', false)
    } catch { done = false }
    if (done) return
    const value = ta.value
    const start = ta.selectionStart ?? value.length
    const end = ta.selectionEnd ?? start
    const next = value.slice(0, start) + insert + value.slice(end)
    setDraft(sessionId, next)
    const pos = start + insert.length
    requestAnimationFrame(() => taRef.current?.setSelectionRange(pos, pos))
  }, [sessionId, setDraft])

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
    const ta = e.currentTarget
    if (suggest) {
      const n = suggest.items.length
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const d = e.key === 'ArrowDown' ? 1 : -1
        setSuggest({ ...suggest, index: (suggest.index + d + n) % n } as Suggest)
        return true
      }
      if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
        e.preventDefault()
        if (suggest.kind === 'cmd') acceptCommand(suggest.items[suggest.index], e.key === 'Enter')
        else acceptFile(suggest.items[suggest.index], suggest)
        return true
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setSuggest(null)
        lastEsc.current = 0
        return true
      }
    }
    if (e.key === 'Tab' && e.shiftKey) {
      // Shift+Tab — следующий режим прав, как в Claude Code
      e.preventDefault()
      setMode(nextMode(MODE_CYCLE.includes(mode) ? mode : 'bypassPermissions'))
      return true
    }
    // токен вставки стирается целиком, как в CLI
    if ((e.key === 'Backspace' || e.key === 'Delete') && ta.selectionStart === ta.selectionEnd) {
      const hit = tokenAt(ta.value, ta.selectionStart, e.key === 'Backspace' ? 'before' : 'after')
      if (hit) {
        e.preventDefault()
        // саму вставку не выкидываем: Ctrl+Z вернёт токен, и он должен
        // остаться рабочим; лишние отсеются при отправке (usedPastes)
        replaceSelection(ta, '', hit.start, hit.end)
        return true
      }
    }
    if (e.key === 'Backspace' && bang && ta.value === '' ) {
      // Backspace в пустом bash-поле — обратно в обычный ввод
      e.preventDefault()
      setBang(false)
      return true
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      const now = Date.now()
      if (now - lastEsc.current < DOUBLE_ESC_MS) {
        lastEsc.current = 0
        setDraftPastes(sessionId, [])
        setDraft(sessionId, '')
        setBang(false)
        histPos.current = null
        return true
      }
      lastEsc.current = now
      if (busy) stop()
      return true
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      const value = ta.value
      const browsing = histPos.current !== null
      const firstLine = !value.slice(0, ta.selectionStart).includes('\n')
      const lastLine = !value.slice(ta.selectionEnd).includes('\n')
      if (e.key === 'ArrowUp' && (value === '' || (browsing && firstLine))) {
        const hist = loadHistory(sessionId)
        if (!hist.length) return false
        e.preventDefault()
        if (!browsing) histDraft.current = { text: bang ? `!${value}` : value, pastes: getDraftPastes(sessionId) }
        const pos = histPos.current === null ? hist.length - 1 : Math.max(0, histPos.current - 1)
        histPos.current = pos
        recall(hist[pos])
        return true
      }
      if (e.key === 'ArrowDown' && browsing && lastLine) {
        e.preventDefault()
        const hist = loadHistory(sessionId)
        const pos = (histPos.current ?? hist.length) + 1
        if (pos >= hist.length) {
          histPos.current = null
          recall(histDraft.current)
        } else {
          histPos.current = pos
          recall(hist[pos])
        }
        return true
      }
    }
    return false
  }, [acceptCommand, acceptFile, bang, busy, mode, recall, replaceSelection, sessionId, setDraft, setMode, stop, suggest])

  const onPaste = useCallback((e: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(e.clipboardData.files)
    if (files.length > 0) {
      e.preventDefault()
      addFiles(files.map(namedPaste))
      return
    }
    // большой текст — токеном `[Pasted text #N +K lines]`, как в Claude Code
    const text = e.clipboardData.getData('text/plain')
    if (!text || !isBigPaste(text)) return
    e.preventDefault()
    const list = getDraftPastes(sessionId)
    const id = list.reduce((m, p) => Math.max(m, p.id), 0) + 1
    const clean = text.replace(/\r\n?/g, '\n')
    setDraftPastes(sessionId, [...list, { id, text: clean }])
    replaceSelection(e.currentTarget, pasteToken(id, clean))
  }, [addFiles, replaceSelection, sessionId])

  // ---------------- голос ----------------

  const startVoice = useCallback(() => {
    if (voicePhase !== 'idle') return
    useLogStore.getState().patchVoice(sessionId, {
      phase: 'requesting', seconds: 0, error: null, bars: new Array(VOICE_BARS).fill(3),
    })
  }, [sessionId, voicePhase])

  // Запасной путь: обычно расшифровку агенту отправляет сервер (autosend,
  // lib/voiceUpload.ts); сюда попадаем только со старым сервером.
  const onTranscript = useCallback((text: string, _seconds: number, voiceId?: string | null) => {
    if (!voiceId) pushLocal(sessionId, { kind: 'voice', gutter: null, text: 'голосовое → расшифровано, отправляю агенту' })
    if (deliverInput(sessionId, text) === 'parked') {
      pushLocal(sessionId, { kind: 'warn', gutter: null, text: 'окно беседы закрыто — расшифровка уйдёт агенту, когда его откроют' })
    }
  }, [pushLocal, sessionId])

  // ---------------- drag & drop (§2в п.1) ----------------

  const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files')
  const dragProps = {
    onDragEnter: (e: DragEvent<HTMLDivElement>) => {
      if (!hasFiles(e)) return
      e.preventDefault()
      dragDepth.current += 1
      setDragging(true)
    },
    onDragOver: (e: DragEvent<HTMLDivElement>) => {
      if (hasFiles(e)) e.preventDefault()
    },
    onDragLeave: (e: DragEvent<HTMLDivElement>) => {
      if (!hasFiles(e)) return
      dragDepth.current = Math.max(0, dragDepth.current - 1)
      if (dragDepth.current === 0) setDragging(false)
    },
    onDrop: (e: DragEvent<HTMLDivElement>) => {
      if (!hasFiles(e)) return
      e.preventDefault()
      dragDepth.current = 0
      setDragging(false)
      addFiles(Array.from(e.dataTransfer.files))
    },
  }

  // ---------------- лог ----------------

  const from = shownFrom ?? Math.max(0, lines.length - WINDOW_LINES)
  const visible = from > 0 ? lines.slice(from) : lines
  const hidden = from

  const modeLabel = PERMISSION_MODE_LABEL[mode]
  const right = [
    modelLabel(session?.model),
    session ? ctxLabel(session.ctxPercent, session.ctxTokens) : '',
    session?.bashContext ? `+${session.bashContext} вывод ! → агенту` : '',
  ].filter(Boolean).join(' · ')

  const connLabel = conn === 'connecting' ? 'подключение…'
    : conn === 'reconnecting' ? 'переподключение…' : conn === 'dead' ? 'связь потеряна' : ''

  const fieldPlaceholder = bashMode
    ? `команда shell — выполнится в ${session?.cwd ?? 'папке сессии'}`
    : ctxNote && lines.length === 0 ? 'спросите о заметке…'
      : placeholder ?? 'сообщение агенту…'

  const suggestBox = suggest ? (
    <div className="cli-suggest" role="listbox">
      {suggest.kind === 'cmd'
        ? suggest.items.map((item, i) => (
          <button
            type="button"
            key={item.name}
            role="option"
            aria-selected={i === suggest.index}
            className={`cli-suggest__row${i === suggest.index ? ' is-on' : ''}`}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => setSuggest({ ...suggest, index: i })}
            onClick={() => acceptCommand(item, true)}
          >
            <span className="cli-suggest__name">
              /{item.name}
              {item.argHint ? <i> {item.argHint.length > 36 ? `${item.argHint.slice(0, 35)}…` : item.argHint}</i> : null}
            </span>
            <span className="cli-suggest__desc">{item.description}</span>
            <span className={`cli-suggest__src is-${item.source}`}>{SOURCE_LABEL[item.source]}</span>
          </button>
        ))
        : suggest.items.map((item, i) => (
          <button
            type="button"
            key={item.path}
            role="option"
            aria-selected={i === suggest.index}
            className={`cli-suggest__row${i === suggest.index ? ' is-on' : ''}`}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => setSuggest({ ...suggest, index: i })}
            onClick={() => acceptFile(item, suggest)}
          >
            <span className="cli-suggest__name">@{item.path}</span>
            <span className="cli-suggest__desc" />
            <span className="cli-suggest__src">{item.dir ? 'папка' : 'файл'}</span>
          </button>
        ))}
    </div>
  ) : null

  const chips = attachments.length ? (
    <div className="cli-chips">
      {attachments.map((a) => (
        <span
          key={a.id}
          className={`cli-chip is-${a.status}${a.executable ? ' is-exec' : ''}`}
          title={a.status === 'error' ? a.error ?? '' : a.executable ? `${a.name} — исполняемый файл` : a.name}
        >
          {a.preview ? <img className="cli-chip__img" src={a.preview} alt="" /> : null}
          <span className="cli-chip__ext">{extOf(a.name)}</span>
          <span className="cli-chip__name">{a.name}</span>
          <span className="cli-chip__size">
            {a.status === 'error' ? (a.error ?? 'ошибка') : humanSize(a.size)}
          </span>
          {a.status === 'error' ? (
            <button type="button" className="cli-chip__retry" onClick={() => upload(a)}>повторить</button>
          ) : null}
          <button type="button" className="cli-chip__x" aria-label={`убрать ${a.name}`} onClick={() => removeFile(a.id)}>×</button>
          {a.status === 'uploading' ? (
            <span className="cli-chip__bar" style={{ width: `${Math.round((a.progress ?? 0) * 100)}%` }} />
          ) : null}
        </span>
      ))}
    </div>
  ) : null

  const ctxPill = ctxNote ? (
    <div className="cli-ctx">
      <span className="cli-ctx__label">в контексте:</span>
      <span className="cli-ctx__pill">
        <button type="button" className="cli-ctx__open" title={ctxNote.path} onClick={() => openNote(ctxNote.path)}>
          📄 {ctxNote.title}
        </button>
        {onDropNote ? (
          <button type="button" className="cli-ctx__x" aria-label="убрать заметку из контекста" onClick={onDropNote}>×</button>
        ) : null}
      </span>
    </div>
  ) : null

  const above = (
    <>
      {exited ? (
        <div className="cli-revive">
          <span>сессия завершена — процесс агента больше не живёт</span>
          <button type="button" disabled={reviving} onClick={() => void relaunch(false)}>
            {reviving ? 'оживляю…' : 'продолжить разговор'}
          </button>
        </div>
      ) : null}
      <VoicePending sessionId={sessionId} />
      {voicePhase === 'idle' ? null : <VoiceBar sessionId={sessionId} onTranscript={onTranscript} />}
      {suggestBox}
      {ctxPill}
      {chips}
    </>
  )

  // отказ до загрузки (§2в п.6: «файл больше 50 МБ — не прикреплён») — под полем
  const below = notice ? (
    <>
      <span className="cli-notice" role="status">{notice}</span>
      <button type="button" className="cli-mode" onClick={() => setNotice(null)}>скрыть</button>
    </>
  ) : (
    <>
      <button
        type="button"
        className={`cli-mode${bashMode ? ' is-bash' : ''}`}
        title="режим прав — Shift+Tab: следующий"
        onClick={(e) => setModeMenu(e.currentTarget)}
      >
        {bashMode
          ? '! режим shell · вывод уйдёт агенту следующим ходом'
          : `⏵⏵ ${modeLabel}${mobile ? '' : ' · shift+tab'}`}
      </button>
      <span className="cli-meta">{right}</span>
    </>
  )

  const actions = (
    <>
      <input
        ref={fileRef}
        className="cli-filepick"
        type="file"
        multiple
        tabIndex={-1}
        onChange={(e) => {
          addFiles(Array.from(e.target.files ?? []))
          e.target.value = ''
        }}
      />
      <CliAttachButton onClick={() => fileRef.current?.click()} />
      <CliVoiceButton onClick={startVoice} active={voicePhase !== 'idle'} />
      {mobile && (draft.trim() || ready.length) ? (
        <button type="button" className="cli-send" aria-label="отправить" disabled={uploading} onClick={() => void submit()}>↑</button>
      ) : null}
    </>
  )

  return (
    <CliWindow dropActive={dragging} className={`cli-win${variant === 'channel' ? ' is-channel' : ''}`} {...dragProps}>
      {connLabel ? (
        <div className="cli-head">
          <span className={`cli-head__conn is-${conn}`}>{connLabel}</span>
        </div>
      ) : null}

      <CliLog ref={logRef}>
        <div className="cli-lines ab-log__inner">
          {hidden > 0 ? (
            <button type="button" className="t-more cli-earlier" onClick={() => setShownFrom(Math.max(0, from - WINDOW_LINES))}>
              … показать раньше ({hidden})
            </button>
          ) : null}
          {lines.length === 0 ? empty ?? null : null}
          {visible.map((line) => (
            <LineView key={line.seq} sessionId={sessionId} line={line} onRespond={respond} />
          ))}
          {session?.tasks?.length ? <AgentsBlock tasks={session.tasks} /> : null}
          {state === 'running' ? <BusyLine onInterrupt={stop} /> : null}
        </div>
      </CliLog>

      <CliInput
        className={bashMode ? 'cli-in is-bash' : 'cli-in'}
        value={draft}
        onChange={onChange}
        onSubmit={() => void submit()}
        submitBlocked={uploading}
        placeholder={fieldPlaceholder}
        prompt={bashMode ? '!' : '>'}
        actions={actions}
        above={above}
        below={below}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        textareaRef={taRef}
      />

      {modeMenu ? (
        <Menu
          open
          anchor={modeMenu}
          placement="top-start"
          onClose={() => setModeMenu(null)}
          header="режим прав · Shift+Tab — следующий"
          items={(Object.keys(PERMISSION_MODE_LABEL) as PermissionMode[]).map((m) => ({
            id: m,
            label: PERMISSION_MODE_LABEL[m],
            hint: m,
            active: m === mode,
            onSelect: () => setMode(m),
          }))}
        />
      ) : null}

      {dialog ? (
        <SaveNoteDialog
          sessionId={sessionId}
          onSaved={() => setDialog(false)}
          onClose={() => setDialog(false)}
        />
      ) : null}
    </CliWindow>
  )
}
